import { Filters } from '@/service-library/request-types'
import { Document, DocumentRow, DocumentRowValue } from '@/types/documents'
import {
  FilterDefinition,
  FilterField,
  FilterSource,
  LiveListConfig,
  LiveListConfigs,
  StoredFilter,
} from '@/types/live-list-filter'

export type ResolveRowValue = (
  row: DocumentRow,
  infoTypeId: string,
) => DocumentRowValue | undefined

export type ResolveDocValue = (
  doc: Document,
  infoTypeId: string,
) => DocumentRowValue | undefined

export interface FilterBuilderOptions {
  storedFilter: StoredFilter | null
  infoTypeId: string
  liveListConfigs: LiveListConfigs
  /** Already-loaded in-memory entities. */
  document?: Document
  documentRow?: DocumentRow
  /**
   * Map of info_type_id -> project_grid_field_id, used by the default
   * value resolvers to find a DocumentRowValue. Required unless you
   * provide custom resolvers.
   */
  projectGridFieldIdByInfoType?: Record<string, string>
  /** Optional overrides (e.g. tests). */
  resolveRowValue?: ResolveRowValue
  resolveDocValue?: ResolveDocValue
  /** Extra keys merged into the final params (paging, cred ids, etc.). */
  extra: Filters | null
}

export interface FilterBuilderResult {
  readonly liveListConfig: LiveListConfig
  readonly definition: FilterDefinition
  readonly liveListUrl: string
  /** Returns the flat `{ field__op: value }` params object. */
  build(): Filters
}

// --- helpers ------------------------------------------------------------

function resolveLiveListConfig(
  infoTypeId: string,
  configs: LiveListConfigs,
): LiveListConfig {
  // Last one wins (matches the Python `configs_by_info_type` behavior).
  let match: LiveListConfig | undefined
  for (const cfg of configs.live_list_configs) {
    if (cfg.info_types.includes(infoTypeId)) match = cfg
  }
  if (!match) {
    throw new Error(`No LiveListConfig registered for info_type ${infoTypeId}`)
  }
  return match
}

function sourceKey(field: FilterField): string {
  const op = field.ops.includes('eq') ? 'eq' : field.ops[0]
  return `${field.name}__${op}`
}

function extractAttr(
  value: DocumentRowValue | undefined,
  attribute: string,
): string | number | boolean | null {
  const json = value?.final_value_json
  if (!json) return null
  const v = json[attribute]
  return v === '' ? null : v ?? null
}

function defaultResolveRowValue(
  fieldByInfoType: Record<string, string>,
): ResolveRowValue {
  return (row, infoTypeId) => {
    const fieldId = fieldByInfoType[infoTypeId]
    if (!fieldId) return undefined
    return row.document_row_values?.find(
      (v) => v.project_grid_field_id === fieldId,
    )
  }
}

function defaultResolveDocValue(
  fieldByInfoType: Record<string, string>,
): ResolveDocValue {
  return (doc, infoTypeId) => {
    const fieldId = fieldByInfoType[infoTypeId]
    if (!fieldId) return undefined
    for (const row of doc.document_rows ?? []) {
      const hit = row.document_row_values?.find(
        (v) => v.project_grid_field_id === fieldId,
      )
      if (hit) return hit
    }
    return undefined
  }
}

export function validateStoredFilter(
  stored: StoredFilter,
  def: FilterDefinition,
): void {
  const allowed = new Map<string, Set<string>>()
  for (const f of def.fields) allowed.set(f.name, new Set(f.ops))

  stored.clauses.forEach((c, i) => {
    const ops = allowed.get(c.field)
    if (!ops) {
      throw new Error(
        `Clause ${i}: unknown field "${c.field}" for resource ${def.resource}`,
      )
    }
    if (!ops.has(c.op)) {
      throw new Error(
        `Clause ${i}: op "${c.op}" not allowed for "${c.field}"; ` +
          `allowed: ${[...ops].sort().join(', ')}`,
      )
    }
  })
}

// --- main ---------------------------------------------------------------

export function createFilterBuilder(
  opts: FilterBuilderOptions,
): FilterBuilderResult {
  const liveListConfig = resolveLiveListConfig(
    opts.infoTypeId,
    opts.liveListConfigs,
  )
  const definition = liveListConfig.filter_config

  const storedFilter: StoredFilter = opts.storedFilter ?? {
    resource: definition.resource,
    resource_version: definition.version,
    schema_version: 1,
    clauses: [],
  }

  const fieldMap = opts.projectGridFieldIdByInfoType ?? {}
  const resolveRow: ResolveRowValue =
    opts.resolveRowValue ?? defaultResolveRowValue(fieldMap)
  const resolveDoc: ResolveDocValue =
    opts.resolveDocValue ?? defaultResolveDocValue(fieldMap)

  function validateEntityRequirements(): void {
    const needsRow = definition.fields.some(
      (f) => f.source?.scope === 'doc_row',
    )
    const needsDoc = definition.fields.some((f) => f.source?.scope === 'doc')

    if (needsRow && !opts.documentRow) {
      throw new Error(
        `Resource "${definition.resource}" has doc_row-scoped ` +
          `FilterSources; \`documentRow\` is required`,
      )
    }
    if (needsDoc && !opts.document && !opts.documentRow?.document) {
      throw new Error(
        `Resource "${definition.resource}" has doc-scoped ` +
          `FilterSources; \`document\` (or \`documentRow.document\`) is required`,
      )
    }
  }

  function readFirstFromRow(
    infoTypes: string[],
    attribute: string,
  ): string | number | boolean | null {
    const row = opts.documentRow as DocumentRow
    for (const it of infoTypes) {
      const value = extractAttr(resolveRow(row, it), attribute)
      if (value != null) return value
    }
    return null
  }

  function readFirstFromDoc(
    infoTypes: string[],
    attribute: string,
  ): string | number | boolean | null {
    const doc = opts.document ?? opts.documentRow?.document
    if (!doc) return null
    for (const it of infoTypes) {
      const value = extractAttr(resolveDoc(doc, it), attribute)
      if (value != null) return value
    }
    return null
  }

  function resolveSourceValue(
    source: FilterSource,
  ): string | number | boolean | null {
    if (source.scope === 'doc_row') {
      return readFirstFromRow(source.info_types, source.attribute)
    }
    if (source.scope === 'doc') {
      return readFirstFromDoc(source.info_types, source.attribute)
    }
    throw new Error(`Unsupported FilterSource scope: ${source.scope}`)
  }

  function build(): Filters {
    validateStoredFilter(storedFilter, definition)

    const params: Filters = {}

    // 1. Stored clauses
    for (const clause of storedFilter.clauses) {
      params[`${clause.field}__${clause.op}`] = String(clause.value)
    }

    // 2. Source-derived fields
    for (const field of definition.fields) {
      if (!field.source) continue
      const key = sourceKey(field)
      if (key in params) continue // caller-set wins

      const value = resolveSourceValue(field.source)
      if (value == null || value === '') {
        if (field.source.required) {
          throw new Error(
            `Required source value missing for field "${field.name}" ` +
              `(resource=${definition.resource})`,
          )
        }
        continue
      }
      params[key] = String(value)
    }

    // 3. Caller extras
    if (opts.extra) Object.assign(params, opts.extra)

    return params
  }

  validateEntityRequirements()

  return {
    liveListConfig,
    definition,
    liveListUrl: liveListConfig.live_list_url,
    build,
  }
}
